import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Card, Dialog, ErrorBanner, PageHeader, Pagination, SearchBox, Spinner, Table } from '../../components/ui';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { useUrlState, type Paged } from '../../lib/hooks';

interface Entry { id: string; action: string; actorName: string | null; actorType: string; targetType: string | null; targetId: string | null; before: unknown; after: unknown; ip: string | null; createdAt: string }

export function Audit() {
  const [f, setF] = useUrlState({ action: '', targetId: '', page: '1' });
  const q = useQuery({ queryKey: ['audit', f], queryFn: () => api<Paged<Entry>>('/admin/audit-logs', { query: { ...f, pageSize: 50 } }), placeholderData: keepPreviousData });
  const [open, setOpen] = useState<Entry | null>(null);
  return (
    <>
      <PageHeader title="Audit log" subtitle="Every change made in the admin panel, who made it, and when. Entries cannot be edited or deleted." />
      <Card>
        <div className="mb-3 flex flex-wrap gap-2">
          <SearchBox value={f.action} onChange={(v) => setF({ action: v })} placeholder="Action, e.g. product.price" />
          <SearchBox value={f.targetId} onChange={(v) => setF({ targetId: v })} placeholder="Target ID" />
        </div>
        {q.isLoading ? <Spinner /> : q.error ? <ErrorBanner error={q.error} /> : (
          <>
            <Table rows={q.data!.items} rowKey={(e) => e.id} onRowClick={setOpen} columns={[
              { header: 'When', cell: (e) => <span className="whitespace-nowrap">{dateTime(e.createdAt)}</span> },
              { header: 'Who', cell: (e) => e.actorName ?? e.actorType },
              { header: 'Action', cell: (e) => <span className="font-mono text-xs">{e.action}</span> },
              { header: 'Target', cell: (e) => <span className="text-xs text-ink-muted">{e.targetType} {e.targetId?.slice(0, 8)}</span> },
            ]} />
            <Pagination page={q.data!.page} totalPages={q.data!.totalPages} total={q.data!.total} onPage={(p) => setF({ page: String(p) } as never)} />
          </>
        )}
      </Card>
      <Dialog open={!!open} wide title={open?.action ?? ''} onClose={() => setOpen(null)}>
        {open && <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><h3 className="mb-1 text-sm font-semibold">Before</h3><pre className="max-h-80 overflow-auto rounded bg-cream p-2 text-xs">{JSON.stringify(open.before, null, 2) ?? '—'}</pre></div>
          <div><h3 className="mb-1 text-sm font-semibold">After</h3><pre className="max-h-80 overflow-auto rounded bg-cream p-2 text-xs">{JSON.stringify(open.after, null, 2) ?? '—'}</pre></div>
          <p className="text-xs text-ink-muted sm:col-span-2">{open.actorName ?? open.actorType} · {dateTime(open.createdAt)} · IP {open.ip ?? '—'} · {open.targetType} {open.targetId}</p>
        </div>}
      </Dialog>
    </>
  );
}
